<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Range, type RangeMarks } from 'vue-range-multi'
import { getRiskConfig, riskConfig } from '../riskConfig'
import RiskMatrix from './RiskMatrix.vue'
import Toolbar from './Toolbar.vue'
import { cloneDeep, isEqual } from 'lodash'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import { useI18n } from 'vue-i18n'
import { parseRiskScore, formatRiskScore, formatRiskScoreStr } from '../shared'
import CatalogsCard from './CatalogsCard.vue'
import type { CUD, Response, RiskCatalogMeta, RiskCategory } from '@/models/api-generated'
import { shallowCloneArray } from '@/services/utils'
import CategoriesCard from './CategoriesCard.vue'

const emit = defineEmits<{ (e: 'changed'): void }>()

const toaster = toastStore()
const { t } = useI18n()
const isEdit = ref(false)

const matrixSection = ref<HTMLElement>()
const riskScoreSection = ref<HTMLElement>()

defineExpose({ isEdit, matrixSection, riskScoreSection })

const isSaving = ref(false)
const originalConfig = ref(cloneDeep(riskConfig.value))
const originalCatalogs = ref<RiskCatalogMeta[]>([])
const catalogsDraft = ref<RiskCatalogMeta[]>([])
const unsavedCatalogs = computed(() => catalogsDraft.value.filter((c) => !c.id))
const deletedCatalogIds = ref<Set<number>>(new Set())

const originalCategories = ref<RiskCategory[]>([])
const categoriesDraft = ref<RiskCategory[]>([])
const deletedCategoryIds = ref<Set<number>>(new Set())
const unsavedCategories = computed(() => categoriesDraft.value.filter((c) => !c.metaId))

const riskLevelRange = computed({
	get: () => riskConfig.value.riskLevels.filter((_, i) => i < riskConfig.value.matrixSize - 1).map((x) => formatRiskScore(x.rangeMax)),
	set: (s: number[]) => {
		s.forEach((_, i) => {
			riskConfig.value.riskLevels[i].rangeMax = parseRiskScore(s[i])
		})
	},
})

const acceptableRiskRange = computed({
	get: () => formatRiskScore(riskConfig.value.acceptableRisk),
	set: (s: number) => (riskConfig.value.acceptableRisk = parseRiskScore(s)),
})

watch(
	() => riskConfig.value.matrixSize,
	(sz) => {
		if (!riskConfig.value.riskLevels.length) return

		riskConfig.value.riskLevels[sz - 1].rangeMax = 1.0

		switch (sz) {
			case 3:
				riskConfig.value.riskLevels[0].rangeMax = 2 / 8
				riskConfig.value.riskLevels[1].rangeMax = 5 / 8
				riskConfig.value.acceptableRisk = 3 / 8
				break
			case 4:
				riskConfig.value.riskLevels[0].rangeMax = 3 / 15
				riskConfig.value.riskLevels[1].rangeMax = 7 / 15
				riskConfig.value.riskLevels[2].rangeMax = 11 / 15
				riskConfig.value.acceptableRisk = 7 / 15
				break
			case 5:
				riskConfig.value.riskLevels[0].rangeMax = 4 / 24
				riskConfig.value.riskLevels[1].rangeMax = 9 / 24
				riskConfig.value.riskLevels[2].rangeMax = 14 / 24
				riskConfig.value.riskLevels[3].rangeMax = 19 / 24
				riskConfig.value.acceptableRisk = 11 / 24
		}
	},
)

const acceptableMarks = computed(() => {
	const marks: RangeMarks = {
		1: '1',
	}

	const f1 = Math.floor((riskConfig.value.matrixSize * riskConfig.value.matrixSize) / 2)
	marks[f1] = f1.toFixed()

	const f2 = riskConfig.value.matrixSize * riskConfig.value.matrixSize
	marks[f2] = f2.toFixed()

	return marks
})

const levelMarks = computed(() => {
	const marks: RangeMarks = {}
	for (let i = 1; i < riskConfig.value.matrixSize * riskConfig.value.matrixSize; i++) {
		marks[i] = i.toFixed()
	}
	return marks
})

const catalogsDraftNormalized = computed(() => catalogsDraft.value.map((c) => ({ ...c, name: c.catalog.name })))

const canSaveCatalogs = computed(
	() =>
		(validItems(catalogsDraftNormalized.value) && !isEqual(originalCatalogs.value, catalogsDraft.value)) ||
		deletedCatalogIds.value.size > 0,
)
const canSaveCategories = computed(
	() =>
		(validItems(categoriesDraft.value) && !isEqual(originalCategories.value, categoriesDraft.value)) || deletedCategoryIds.value.size > 0,
)
const canSaveConfig = computed(() => !isEqual(riskConfig.value, originalConfig.value))
const canSave = computed(() => canSaveCatalogs.value || canSaveCategories.value || canSaveConfig.value)

async function reset() {
	isEdit.value = false
	deletedCatalogIds.value.clear()
	catalogsDraft.value = cloneDeep(originalCatalogs.value)
	deletedCategoryIds.value.clear()
	categoriesDraft.value = cloneDeep(originalCategories.value)
	riskConfig.value = await getRiskConfig()
	originalConfig.value = cloneDeep(riskConfig.value)
}

const fetchCatalogs = async () => {
	const { data } = await http.get<Response<RiskCatalogMeta[]>>('/api/risk/catalogs')

	if (data.data) {
		originalCatalogs.value = cloneDeep(data.data)
		catalogsDraft.value = cloneDeep(data.data)
	}
}

const fetchCategories = async () => {
	const { data } = await http.get<Response<RiskCategory[]>>('/api/risk/categories')

	if (data.data) {
		originalCategories.value = data.data
		categoriesDraft.value = shallowCloneArray(data.data)
	}
}

const saveCatalogs = async () => {
	const created = unsavedCatalogs.value
	const updated = catalogsDraft.value.filter((c) => {
		const o = originalCatalogs.value.find((x) => x.id === c.id)
		return o && !isEqual(o, c)
	})
	const deleted = catalogsDraft.value.filter((c) => deletedCatalogIds.value.has(c.id))

	const payload: CUD<RiskCatalogMeta> = { created, updated, deleted }

	await http.post('/api/risk/catalog/cud', payload)
	await fetchCatalogs()
}

const saveCategories = async () => {
	const created = unsavedCategories.value
	const updated = categoriesDraft.value.filter((c) => {
		const o = originalCategories.value.find((x) => x.metaId === c.metaId)
		return o && !isEqual(o, c)
	})
	const deleted = categoriesDraft.value.filter((c) => deletedCategoryIds.value.has(c.metaId))

	const payload: CUD<RiskCategory> = { created, updated, deleted }

	await http.post('/api/risk/category/cud', payload)
	await fetchCategories()
}

onMounted(fetchCatalogs)
onMounted(fetchCategories)

async function save() {
	isSaving.value = true

	await saveCatalogs()
	await saveCategories()

	const { status } = await http.post('/api/risk/config', riskConfig.value)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyEdited'))
		await reset()
	}

	emit('changed')

	isSaving.value = false
}

const validItems = <T extends { name: string }>(items: T[]) => items.every((i) => i.name.length > 0)
</script>

<template>
	<div class="row">
		<div class="col">
			<CatalogsCard v-model="catalogsDraft" :is-edit="isEdit" v-model:deletedCatalogIds="deletedCatalogIds"></CatalogsCard>
		</div>
	</div>

	<div class="row">
		<div class="col">
			<CategoriesCard v-model="categoriesDraft" :is-edit="isEdit" v-model:deletedCategoryIds="deletedCategoryIds"></CategoriesCard>
		</div>
	</div>

	<div class="row" ref="riskScoreSection">
		<div class="col">
			<div class="card p-3 m-3">
				<div class="row mb-2">
					<div class="col">
						<h3>{{ $t('risk.score.label') }}</h3>
					</div>
				</div>
				<div class="row mb-3">
					<div class="col-xl-3 text-end">
						{{ $t('risk.riskMatrix') }} &nbsp;<b>{{ riskConfig.matrixSize }} x {{ riskConfig.matrixSize }}</b>
					</div>
					<div class="col-xl-3">
						<Range
							v-if="isEdit"
							data-test="risk-matrix-size-slider"
							class="p-2 mb-3"
							v-model="riskConfig.matrixSize"
							:min="3"
							:max="5"
							thumb-size="large"
							:show-stops="true"
							:marks="{ 3: '3', 4: '4', 5: '5' }"
						/>
					</div>
				</div>

				<div class="row mb-3">
					<div class="col-xl-3 text-end">
						{{ $t('risk.acceptable') }} &nbsp;<b>{{ formatRiskScoreStr(riskConfig.acceptableRisk) }}</b>
					</div>

					<div class="col-xl-3">
						<Range
							v-if="isEdit"
							data-test="risk-acceptable-slider"
							class="p-2 mb-3"
							v-model="acceptableRiskRange"
							:min="1"
							:max="riskConfig.matrixSize * riskConfig.matrixSize"
							thumb-size="large"
							:show-stops="true"
							:marks="acceptableMarks"
						/>
					</div>
				</div>

				<div class="row text-center">
					<template v-for="(l, i) in riskConfig.riskLevels" :key="i">
						<div v-if="i <= riskConfig.matrixSize - 1" class="col d-flex">
							<div class="card flex-fill" :style="`border-right: 1em solid ${riskConfig.riskLevels[i].color}`">
								<div class="card-body d-flex flex-column align-items-center justify-content-between">
									<template v-if="!isEdit">
										<h5 class="card-title">{{ l.title }}</h5>
										<p class="card-text">{{ l.description }}</p>
									</template>
									<template v-else>
										<input
											:data-test="`risk-level-title-${i}`"
											class="form-control w-100 fw-bold"
											type="text"
											v-model="riskConfig.riskLevels[i].title"
											maxlength="50"
										/>
										<textarea
											:data-test="`risk-level-description-${i}`"
											class="form-control w-100"
											v-model="riskConfig.riskLevels[i].description"
											maxlength="700"
										></textarea>
										<input
											:data-test="`risk-level-color-${i}`"
											class="form-control w-100"
											type="color"
											v-model="riskConfig.riskLevels[i].color"
										/>
									</template>
									<h3 class="card-link">
										<template v-if="i === 0">1</template>
										<template v-else>
											{{ (formatRiskScore(riskConfig.riskLevels[i - 1].rangeMax) + 1).toFixed() }}
										</template>
										-
										{{ formatRiskScore(l.rangeMax).toFixed() }}
									</h3>
								</div>
							</div>
						</div>
					</template>
				</div>

				<div class="row p-3" v-if="isEdit">
					<div class="col">
						<Range
							class="p-2"
							data-test="risk-level-scale-slider"
							v-model="riskLevelRange"
							:min="1"
							:max="riskConfig.matrixSize * riskConfig.matrixSize - 1"
							:show-stops="true"
							:marks="levelMarks"
							size="medium"
							thumb-size="large"
							thumb-type="circle"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>

	<div class="row">
		<div class="col">
			<div class="card p-3 m-3" ref="matrixSection">
				<div class="row mb-3">
					<div class="col">
						<h3>{{ $t('risk.riskMatrix') }}</h3>
					</div>
				</div>

				<div class="row">
					<div class="col">
						<RiskMatrix class="w-100" :is-edit="isEdit"></RiskMatrix>
					</div>
				</div>
			</div>
		</div>
	</div>

	<Toolbar v-if="isEdit" :can-save="canSave" :is-saving="isSaving" @reset="reset" @save="save" />
</template>

<style lang="sass" scoped>
.card-text
  white-space: pre-wrap
</style>
